import React, { useEffect, useState } from 'react';
import { FaCalendarAlt, FaUser, FaTag, FaArrowRight } from 'react-icons/fa';
import Seo from '../../components/Seo/Seo';
import PortableTextContent from '../../components/PortableTextContent/PortableTextContent';
import { getBlogPosts } from '../../utils/sanity';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await getBlogPosts();
        setPosts(data || []);
      } catch (err) {
        console.error('Error fetching blog posts from Sanity:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Recently';
    }

    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const openPost = (post) => {
    setSelectedPost(post);
    window.scrollTo(0, 0);
  };

  const closePost = () => {
    setSelectedPost(null);
  };

  if (selectedPost) {
    return (
      <div>
        <Seo
          title={selectedPost.title}
          description={selectedPost.excerpt}
          canonicalPath={`/blog/${selectedPost.slug}`}
        />
        <div className="page-header">
          <div className="container-custom">
            <h1 className="text-2xl md:text-4xl">{selectedPost.title}</h1>
            <div className="flex items-center justify-center gap-6 mt-4 text-primary-200 text-sm">
              <span className="flex items-center gap-2">
                <FaUser />
                {selectedPost.author || 'The Skilled Group Team'}
              </span>
              <span className="flex items-center gap-2">
                <FaCalendarAlt />
                {formatDate(selectedPost.createdAt || selectedPost._createdAt)}
              </span>
            </div>
          </div>
        </div>

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <button
                onClick={closePost}
                className="flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium mb-8 transition-colors"
              >
                <FaArrowRight className="rotate-180 text-sm" />
                Back to All Posts
              </button>

              {selectedPost.tags && selectedPost.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {selectedPost.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 bg-primary-50 text-primary-500 text-xs font-medium px-3 py-1.5 rounded-full"
                    >
                      <FaTag className="text-[10px]" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-gray-800 prose-p:text-gray-600 prose-p:leading-relaxed prose-li:text-gray-600">
                <PortableTextContent value={selectedPost.content} />
              </div>

              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="bg-primary-50 rounded-2xl p-6 md:p-8 text-center border border-primary-100">
                  <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">
                    Need Professional Cleaning?
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 max-w-md mx-auto">
                    The Skilled Group offers all the services mentioned in this article. Book today for a spotless clean.
                  </p>
                  <a href="/booking" className="btn-primary inline-flex items-center gap-2">
                    Book a Cleaning
                    <FaArrowRight className="text-sm" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <Seo
        title="Blog"
        description="Read practical pest control and cleaning tips, service updates, and advice from The Skilled Group team."
        canonicalPath="/blog"
      />
      <div className="page-header">
        <div className="container-custom">
          <h1>Our Blog</h1>
          <p>Tips, insights, and news about professional cleaning from The Skilled Group team.</p>
        </div>
      </div>

      <section className="section-padding bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <svg className="animate-spin h-12 w-12 text-primary-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500 text-lg">{error}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-3">No Blog Posts Yet</h2>
              <p className="text-gray-500">Add a post in Sanity and it will appear here automatically.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <article key={post._id} className="card group cursor-pointer" onClick={() => openPost(post)}>
                  <div className="h-48 bg-primary-50 flex items-center justify-center relative overflow-hidden">
                    <div className="text-center p-4">
                      <p className="text-primary-300 font-heading font-semibold text-sm">The Skilled Group Blog</p>
                    </div>
                    <div className="absolute inset-0 bg-primary-500 bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300" />
                  </div>

                  <div className="p-6">
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="bg-primary-50 text-primary-500 text-xs font-medium px-2.5 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <h2 className="text-lg font-heading font-semibold text-gray-800 mb-3 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <FaUser />
                          {post.author || 'The Skilled Group Team'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FaCalendarAlt />
                          {formatDate(post.createdAt || post._createdAt)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4">
                      <span className="inline-flex items-center gap-2 text-primary-500 font-semibold text-sm group-hover:text-accent-500 transition-colors">
                        Read More
                        <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;